import {h} from 'preact'
import { useState, useEffect, useCallback } from 'preact/hooks';
import AsyncSelect from 'react-select/async';
import axios from "axios";

const ConsumerSelector = ({ onConsumerSelect, selectedConsumerId }) => {
    const [selectedConsumer, setSelectedConsumer] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const loadOptions = useCallback(async (inputValue) => {
        if (!inputValue || inputValue.length < 2) {
            return [];
        }

        try {
            setIsLoading(true);
            const response = await axios.get("/admin/consumers", {
                params: {
                    username: inputValue,
                    limit: 20,
                    offset: 0
                }
            });

            const consumers = response.data || [];
            return consumers.map(consumer => ({
                value: consumer.consumer_id,
                label: `${consumer.username} (${consumer.consumer_id})`,
                consumer: consumer
            }));
        } catch (error) {
            console.error("Error searching consumers:", error);
            return [];
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (selectedConsumerId && !selectedConsumer) {
            const loadSelectedConsumer = async () => {
                try {
                    const response = await axios.get(`/admin/consumer/${selectedConsumerId}`);
                    const consumer = response.data;
                    const option = {
                        value: consumer.consumer_id,
                        label: `${consumer.username} (${consumer.consumer_id})`,
                        consumer: consumer
                    };
                    setSelectedConsumer(option);
                } catch (error) {
                    console.error("Error loading selected consumer:", error);
                }
            };
            loadSelectedConsumer();
        }
    }, [selectedConsumerId, selectedConsumer]);

    const handleConsumerSelect = (option) => {
        setSelectedConsumer(option);
        onConsumerSelect(option?.value || null);
    };

    // Custom styles for react-select
    const customStyles = {
        container: (provided) => ({
            ...provided,
            width: '350px',
            maxWidth: '100%'
        }),
        control: (provided) => ({
            ...provided,
            minHeight: '40px',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            '&:hover': {
                border: '1px solid #9ca3af'
            }
        }),
        menu: (provided) => ({
            ...provided,
            zIndex: 9999,
            width: '100%'
        }),
        option: (provided, state) => ({
            ...provided,
            padding: '8px 12px',
            fontSize: '14px',
            backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? '#f3f4f6' : 'white',
            color: state.isSelected ? 'white' : '#374151',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
        }),
        placeholder: (provided) => ({
            ...provided,
            color: '#9ca3af'
        }),
        singleValue: (provided) => ({
            ...provided,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
        }),
        noOptionsMessage: (provided) => ({
            ...provided,
            padding: '8px 12px',
            color: '#6b7280'
        }),
        loadingMessage: (provided) => ({
            ...provided,
            padding: '8px 12px',
            color: '#6b7280'
        })
    };

    return (
        <div className="consumer-selector" style={{
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
        }}>
            <AsyncSelect
                cacheOptions
                loadOptions={loadOptions}
                value={selectedConsumer}
                onChange={handleConsumerSelect}
                placeholder="Type to search consumers (min 2 characters)..."
                isClearable
                isLoading={isLoading}
                isSearchable
                styles={customStyles}
                noOptionsMessage={({ inputValue }) => {
                    if (!inputValue) return "Start typing to search consumers...";
                    if (inputValue.length < 2) return "Type at least 2 characters to search";
                    return `No consumers found matching "${inputValue}"`;
                }}
                loadingMessage={() => "Searching consumers..."}
                defaultOptions={false}
                menuIsOpen={undefined}
                onInputChange={(inputValue, { action }) => {
                    if (action === 'input-change') {
                        return inputValue;
                    }
                }}
                components={{
                    DropdownIndicator: () => null,
                    IndicatorSeparator: () => null
                }}
            />
        </div>
    );
};

export default ConsumerSelector;